import type { Layout } from '../../../data/types';
import type { Vec2 } from '../../../geo/types';
import { centroid } from '../../../geo/polygon';
import type { SiteModel } from '../../../site/types';
import type { PlannedLots } from '../../fabric/fabricLots';
import type { FrontDoor } from '../entrances';
import type { LifeEntrance } from '../types';
import {
  cloisterGates,
  nodeAt,
  nodesNear,
  ORDERS,
  PLACE_KINDS,
  PLACES,
  planPlaces,
  type Draft,
  type PlannedPlaces,
} from './crowdPlaces';
import { ROLES } from '../citizens/citizenActing';
import { groundOf, passable, planWalkNet, roomAt, type BakedWalkNet, type Ground } from './walkNet';

export { ORDERS, PLACE_KINDS, PLACES, type Order, type PlaceKind } from './crowdPlaces';

/**
 * What the crowd needs of the city, baked with the other plans: the net it walks, each door with
 * the point of the net before it, the places it goes to and where it stands at them.
 *
 * `doors` is x, y, the way out (x, y) and the point of the net: the houses' street doors first,
 * `houseDoors` of them, then a door for each religious house. `cloisters` is a door and the order
 * (ORDERS) that lives behind it. `places` is kind, point and how far round the point a figure may
 * stand. `seats` is place, the way from its point (x, y), facing, role (ROLES) and company.
 */
export interface BakedCrowd {
  readonly net: BakedWalkNet;
  readonly doors: readonly number[];
  readonly doorways?: readonly (LifeEntrance | null)[];
  readonly houseDoors: number;
  readonly cloisters: readonly number[];
  readonly places: readonly number[];
  readonly seats: readonly number[];
}

const round = (v: number): number => Math.round(v * 100) / 100;

/* Keeps the pieces of the net that a door stands on, and renumbers what refers to its points. */
function pruned(
  draft: Draft,
  ground: Ground,
  doors: readonly (readonly [FrontDoor, number])[],
  planned: PlannedPlaces
): Pick<BakedCrowd, 'net' | 'doors' | 'doorways' | 'places' | 'seats'> {
  const count = draft.nodes.length / 3;
  const parent = Array.from({ length: count }, (_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) i = parent[i] = parent[parent[i]!]!;
    return i;
  };
  for (let e = 0; e < draft.edges.length; e += 2)
    parent[find(draft.edges[e]!)] = find(draft.edges[e + 1]!);
  const lived = new Set(doors.map(([, node]) => find(node)));
  const renumbered = new Int32Array(count).fill(-1);
  const nodes: number[] = [];
  for (let i = 0; i < count; i++) {
    if (!lived.has(find(i))) continue;
    renumbered[i] = nodes.length / 3;
    nodes.push(round(draft.nodes[i * 3]!), round(draft.nodes[i * 3 + 1]!), draft.nodes[i * 3 + 2]!);
  }
  const edges: number[] = [];
  for (let e = 0; e < draft.edges.length; e += 2) {
    const [a, b] = [renumbered[draft.edges[e]!]!, renumbered[draft.edges[e + 1]!]!];
    if (a >= 0 && b >= 0) edges.push(a, b);
  }
  const places: number[] = [];
  const placed = planned.places.map(([kind, node]) => {
    if (renumbered[node]! < 0) return -1;
    places.push(PLACE_KINDS.indexOf(kind), renumbered[node]!, roomAt(nodeAt(draft, node), ground));
    return places.length / 3 - 1;
  });
  const companies = new Map<number, number>();
  const seats: number[] = [];
  for (const seat of planned.seats) {
    if (placed[seat.place]! < 0) continue;
    if (!companies.has(seat.group)) companies.set(seat.group, companies.size);
    const point = nodeAt(draft, planned.places[seat.place]![1]);
    seats.push(
      placed[seat.place]!,
      round(seat.at[0] - point[0]),
      round(seat.at[1] - point[1]),
      round(seat.facing),
      ROLES.indexOf(seat.role),
      companies.get(seat.group)!
    );
  }
  return {
    net: { nodes, edges },
    doors: doors.flatMap(([door, node]) => [
      round(door.position[0]),
      round(door.position[1]),
      round(door.outward[0]),
      round(door.outward[1]),
      renumbered[node]!,
    ]),
    doorways: doors.map(([door]) => door.entrance ?? null),
    places,
    seats,
  };
}

/* The house doors nearest the beguines' chapel, behind which they live. */
function beguineDoors(layout: Layout, doors: readonly (readonly [FrontDoor, number])[]): number[] {
  const chapel = layout.landmarks.find((landmark) => /begijnhof/.test(landmark.id));
  if (!chapel) return [];
  const middle = centroid(chapel.footprint);
  return doors
    .map(([door], i): [number, number] => [
      Math.hypot(door.position[0] - middle[0], door.position[1] - middle[1]),
      i,
    ])
    .filter(([gap]) => gap <= PLACES.beguineReachM)
    .sort((a, b) => a[0] - b[0] || a[1] - b[1])
    .slice(0, PLACES.beguineDoors)
    .map(([, i]) => i);
}

/** Plans what is baked for the crowd. A house whose door reaches no street keeps no residents. */
export function planCrowd(
  layout: Layout,
  site: SiteModel,
  lots: PlannedLots,
  frontDoors: readonly FrontDoor[]
): BakedCrowd {
  const ground = groundOf(layout, site, lots);
  const draft = planWalkNet(layout, ground);
  const doors: [FrontDoor, number][] = [];
  for (const door of frontDoors) {
    const step: Vec2 = [
      door.position[0] + door.outward[0] * PLACES.doorStepM,
      door.position[1] + door.outward[1] * PLACES.doorStepM,
    ];
    const node = nodesNear(draft, step, PLACES.doorReachM).find((i) =>
      passable(step, nodeAt(draft, i), ground.open)
    );
    if (node !== undefined) doors.push([door, node]);
  }
  const houseDoors = doors.length;
  const cloisters = beguineDoors(layout, doors).flatMap((door) => [
    door,
    ORDERS.indexOf('beguine'),
  ]);
  const gates = cloisterGates(layout, draft, ground);
  for (const gate of gates) {
    cloisters.push(doors.length, ORDERS.indexOf(gate.order));
    doors.push([{ position: gate.position, outward: gate.outward }, gate.node]);
  }
  const planned = planPlaces(layout, draft, ground, gates, (p) => site.queries.isWater(p));
  return { ...pruned(draft, ground, doors, planned), houseDoors, cloisters };
}
